<template>
  <div class="prompt">
    <b-field class="prompt__analytics prompt__group">
      <b-switch
        :value="trackAnalytics"
        @input="
          fieldChangeHandler({
            target: { name: 'trackAnalytics', value: $event }
          })
        "
      >
        <b>Track Analytics</b>
      </b-switch>
    </b-field>
    <b-field class="prompt__group" label="Content Type">
      <v-select
        :key="contentType"
        :clearable="false"
        :options="contentTypes"
        placeholder="Select Message Type"
        :reduce="option => option.value"
        :value="contentType"
        @input="
          fieldChangeHandler({
            target: { name: 'contentType', value: $event }
          })
        "
      />
    </b-field>
    <b-field class="prompt__group" label="Message">
      <mc-markdown-editor
        :value="message"
        @input="
          fieldChangeHandler({
            target: { name: 'message', value: $event }
          })
        "
      />
    </b-field>
    <b-field class="prompt__group" label="Input Display Type">
      <v-select
        :key="inputDisplayType"
        :clearable="false"
        :options="inputDisplayTypes"
        placeholder="Select Input Display Type"
        :reduce="option => option.value"
        :value="inputDisplayType"
        @input="
          fieldChangeHandler({
            target: { name: 'inputDisplayType', value: $event }
          })
        "
      />
    </b-field>
    <b-field :addons="false" class="prompt__answers" group label="Answers">
      <b-switch
        class="prompt__toggle prompt__group"
        :value="hasSharedAnswer"
        @input="
          fieldChangeHandler({
            target: { name: 'hasSharedAnswer', value: $event }
          })
        "
      >
        Use Shared Answers
      </b-switch>
      <v-select
        v-if="hasSharedAnswer"
        :clearable="false"
        :options="sharedAnswers"
        placeholder="Select Shared Answer"
        :value="sharedAnswer"
        @input="
          fieldChangeHandler({
            target: { name: 'sharedAnswer', value: $event }
          })
        "
      />
      <fragment v-else>
        <mc-dnd-list
          v-if="answers.length > 0"
          container-class="prompt__answers-list"
          drag-handle-selector=".answer__drag-handle"
          :get-ghost-parent="getGhostParent"
          :list="answers"
          :on-drop="moveAnswerHandler"
        >
          <template #item="{ item, index }">
            <answer-editor
              :answer="item"
              :index="index"
              :prompt-keys="promptKeys"
              :on-change="changeAnswerHandler"
              :on-delete="deleteAnswerHandler"
            />
          </template>
        </mc-dnd-list>
        <b-button
          class="is-fullwidth"
          type="is-light"
          @click="addAnswerHandler"
        >
          Add Answer
        </b-button>
      </fragment>
    </b-field>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';

import AnswerEditor from './AnswerEditor';
import { moveByElement, removeByIndex } from '../../utils/array';

export default {
  components: {
    AnswerEditor
  },
  props: {
    definition: Object,
    promptKey: String,
    onSubmit: {
      type: Function,
      // eslint-disable-next-line
      default: (promptData, promptKey) => {
        /* Handle prompt submit */
      }
    }
  },
  data: function() {
    return {
      inputDisplayTypes: [
        {
          label: 'Default',
          value: 'default'
        },
        {
          label: 'Large',
          value: 'large'
        }
      ],
      inputDisplayType: 'large',
      answers: [],
      hasSharedAnswer: false,
      message: '',
      contentType: 'default',
      contentTypes: [
        {
          label: 'Default',
          value: 'default'
        },
        {
          label: 'Event',
          value: 'event'
        },
        {
          label: 'Video',
          value: 'video'
        }
      ],
      sharedAnswer: '',
      trackAnalytics: false
    };
  },
  computed: {
    promptKeys() {
      return Object.keys(this.definition.prompts);
    },
    sharedAnswers() {
      return Object.keys(this.definition.answers);
    }
  },
  mounted() {
    this.updatePrompt();
  },
  methods: {
    addAnswerHandler() {
      this.answers.push({
        message: '',
        nextKey: ''
      });
      this.triggerOnChange();
    },
    changeAnswerHandler(data, index) {
      this.answers[index] = data;
      this.triggerOnChange();
    },
    deleteAnswerHandler(index) {
      removeByIndex(this.answers, index);
      this.triggerOnChange();
    },
    fieldChangeHandler({ target }) {
      this[target.name] = target.value;
      this.triggerOnChange();
    },
    getGhostParent() {
      return document.body;
    },
    moveAnswerHandler({ fromItem, toItem }) {
      moveByElement(this.answers, fromItem, toItem);
      this.triggerOnChange();
    },
    triggerOnChange() {
      const data = {
        message: this.message,
        trackAnalytics: this.trackAnalytics
      };
      if (this.hasSharedAnswer) {
        data.answers = this.sharedAnswer || null;
      } else {
        data.answers = this.answers;
      }

      if (this.contentType && this.contentType !== 'default') {
        data.contentType = this.contentType;
      }

      if (this.inputDisplayType && this.inputDisplayType !== 'default') {
        data.inputDisplayType = this.inputDisplayType;
      }

      this.$emit('change', {
        data,
        key: this.promptKey
      });
    },
    updatePrompt() {
      const prompt = cloneDeep(this.definition.prompts[this.promptKey]);

      this.message = prompt.message;
      // !! is used to convert to boolean
      this.trackAnalytics = Boolean(prompt.trackAnalytics);
      this.contentType = prompt.contentType || 'default';
      this.inputDisplayType = prompt.inputDisplayType || 'default';

      this.hasSharedAnswer = !Array.isArray(prompt.answers);
      if (this.hasSharedAnswer) {
        this.sharedAnswer = prompt.answers;
      } else {
        this.answers = prompt.answers;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.prompt {
  padding: 1rem;

  &__group {
    margin-bottom: 1rem;
  }

  &__analytics {
    display: flex;
    flex-direction: column;
  }

  &__answers {
    display: flex;
    flex-direction: column;
  }

  &__answers-list {
    margin-bottom: 1rem;

    & /deep/ .dnd {
      &__draggable {
        padding: 0;
        box-shadow: none;
        overflow: visible !important;
      }
    }
  }

  &__toggle {
    margin-bottom: 0.5rem;
  }
}
</style>
